import { Injectable, OnDestroy } from '@angular/core';
import { NavigationCancel, Router } from '@angular/router';

import {
    BehaviorSubject,
    combineLatest, filter as rxjsFilter,
    map,
    mapTo, Observable,
    of,
    Subject,
    Subscription, take,
    tap
} from 'rxjs';

import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { LeftSidebarLocalizations } from '../../../Common/Layout/models/left-sidebar-localizations.model';
import { LeftSidebarService } from '../../../Common/Layout/services/left-sidebar.service';
import { Mode } from '../../../Common/Layout/enums/mode.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { ReportListItemModel } from '../../../Common/Reports/models/report-list-item.model';
import { ReportsService } from '../../../Common/Reports/services/reports.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';

import { ReportCreateDialogComponent } from '../../../Common/Reports/components/ReportCreateDialog/report-create-dialog.component';

import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { FilterTypeRadioModel } from '../../../Common/Filter/models/filter-type-radio.model';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { ListEntitiesVisibilityEnum } from '../../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { ReportsRouteTypeEnum } from '../../../Common/Reports/enums/reports-route-type.enum';
import { FilterOptionEnumLabel } from '../../../Common/MonitorShared/misc/filter-option-enum-label';
import { FilterTypeProviderServiceInterface } from '../../../Common/MonitorShared/misc/filter-type-provider-service.interface';
import { MonitorReportsNavigationService } from '../../../Common/MonitorShared/services/monitor-reports-navigation.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { MentionSearchListItemModel } from '../../../Monitor/Shared/models/mention-search-list-item.model';
import { automationAgentsRoutePath } from '../../../AutomationAgents/AutomationAgentsRouter/automation-agents-route.const';
import { ReportsRouteModel } from '../../ReportsRouter/models/reports-route.model';
import { ReportsRoutingService } from './reports-routing.service';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';

type GenericLeftSidebarItem = AuditableModel & LeftSidebarListItemConvertableModel;

enum DropdownValues {
    mentions,
    topics,
    briefings,
    alerts,
    mediaSubscriptions,
    reports,
    newSearch,
    automationAgents
}

@Injectable()
export class ReportsLeftSidebarService extends LeftSidebarService implements OnDestroy, FilterTypeProviderServiceInterface {
    private allSharedReportsKey: number = -1;
    private dropdownItems: DropdownOptionModel[];
    private dropdownItemsProvider: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);
    private selectedDropdownItem: DropdownOptionModel;
    private selectedDropdownItemProvider: BehaviorSubject<DropdownOptionModel> = new  BehaviorSubject<DropdownOptionModel>(null);

    private hiddenListItems: LeftSidebarListItem[];
    private hiddenListItemsProvider: BehaviorSubject<LeftSidebarListItem[]>;
    private listIsComplete: boolean;
    private listIsCompleteProvider: BehaviorSubject<boolean>;
    private selectedListItem: any | null;
    private selectedListItemProvider: BehaviorSubject<any | null>;
    private reorderEnabled: boolean;
    private reorderEnabledProvider: BehaviorSubject<boolean>;

    private localizations: LeftSidebarLocalizations;
    private localizationsProvider: BehaviorSubject<LeftSidebarLocalizations>;

    private readonly reports: ReportListItemModel[] = [];

    private itemActionHide: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-eye-slash',
        key: ActionsEnum.hide,
        tooltip: 'Monitor.leftSidebar.listItemAction.hide',
        label: 'Lhp.actions.hide',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.hide, icon: ''})
    };
    private itemActionShow: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-eye',
        key: ActionsEnum.show,
        tooltip: 'Monitor.leftSidebar.listItemAction.show',
        label: 'Lhp.actions.show',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.show, icon: ''})
    };

    private itemActionEdit: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-pen',
        key: ActionsEnum.edit,
        tooltip: 'Monitor.leftSidebar.listItemAction.edit',
        label: 'Lhp.actions.rename',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.edit, icon: ''})
    };
    private itemActionDelete: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-trash-can',
        key: ActionsEnum.delete,
        tooltip: 'Monitor.leftSidebar.listItemAction.delete',
        label: 'Lhp.actions.delete',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.delete, icon: ''})
    };
    private itemActionCopy: LeftSidebarListItemButton = {
        icon: 'fa-kit-duotone fa-agpr-copy',
        key: ActionsEnum.copy,
        tooltip: 'Monitor.leftSidebar.listItemAction.copy',
        label: 'Lhp.actions.copy',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.copy, icon: ''})
    };
    private itemActionMove: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.move,
        tooltip: 'Monitor.leftSidebar.listItemAction.move',
        label: 'Lhp.actions.move',
        command: (event: any) => this.moveListItem(event.data)
    };
    private itemActionFavorite: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.favorite,
        tooltip: 'Monitor.leftSidebar.listItemAction.favorite',
        label: 'Lhp.actions.favorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.favorite, icon: ''})
    };
    private itemActionUnfavorite: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.unfavorite,
        tooltip: 'Monitor.leftSidebar.listItemAction.unfavorite',
        label: 'Lhp.actions.unfavorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.unfavorite, icon: ''})
    };

    private showNewReportDialogProvider: Subject<boolean>;
    private currentRoute: ReportsRouteModel;
    private subscriptions: Subscription[] = [];


    private readonly reportConfig =  {
        routeTypes: [
            ReportsRouteTypeEnum.reportsHype,
            ReportsRouteTypeEnum.report,
            ReportsRouteTypeEnum.reportSharedReports,
        ],
        localizations: {
            amplifyButton: 'Reports.leftSideBar.reports.amplifyButton',
            itemsHeading: 'Reports.leftSideBar.reports.itemsHeading',
            statusHeading: 'Reports.leftSideBar.reports.statusHeading',
            noItems: 'Reports.leftSideBar.reports.noItems'
        },
        quickFilterPlaceholder: 'Reports.leftSidebar.reports.filter.quick',
        filters: [
            this.nameFilter,
            this.createdByFilter,
            this.createdDateFilter,
            this.modifiedByFilter,
            this.modifiedDateFilter,
            this.visibilityFilter,
            this.displayGroupFilter
        ],
        filterOptions: [
            { filterId: 'name', prefProp: 'name', label: 'Reports.input.reportName' },
        ],
        listItemSource: this.reports,
        listItemStream: this.reportsService.getReportListItems(),
        convertToSidebarListItems: (items: LeftSidebarListItemConvertableModel[]) => {
            const buttons = [
                this.itemActionEdit,
                this.itemActionCopy,
                this.itemActionFavorite,
                this.itemActionUnfavorite,
                this.itemActionMove,
                this.itemActionHide,
                this.itemActionShow,
                this.itemActionDelete
            ];

            const convertedItems = items.map(item => item.toLeftSidebarListItem(buttons));
            return convertedItems;
        },
    };

    private readonly allSharedReportsItem: LeftSidebarListItem;

    constructor(
        private reportsRoutingService: ReportsRoutingService,
        private monitorReportsNavigationService: MonitorReportsNavigationService,
        private router: Router,
        private reportsService: ReportsService,
        protected usersService: UsersService,
        private currentUserStateService: CurrentUserStateService,
        private coreDialogService: CoreDialogService,
        private userPreferencesService: UserPreferencesService,
        protected displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService
    ) {
        super(usersService, displayGroupsService, localizationService);
        this.currentMode = Mode.reports;

        this.setFilters([
            this.nameFilter,
            this.createdByFilter,
            this.modifiedByFilter,
            this.visibilityFilter
        ]);

        this.showNewReportDialogProvider = new Subject<boolean>();

        this.listItems = null;
        this.listItemsProvider = new BehaviorSubject(this.listItems);
        this.listItemGroupsProvider = new BehaviorSubject(this.listItemGroups);
        this.hiddenListItems = null;
        this.hiddenListItemsProvider = new BehaviorSubject(this.hiddenListItems);
        this.listIsComplete = true;
        this.listIsCompleteProvider = new BehaviorSubject(this.listIsComplete);
        this.selectedListItem = null;
        this.selectedListItemProvider = new BehaviorSubject(this.selectedListItem);
        this.reorderEnabled = false;
        this.reorderEnabledProvider = new BehaviorSubject(this.reorderEnabled);

        this.localizations = this.reportConfig.localizations;
        this.localizationsProvider = new BehaviorSubject(this.localizations);

        this.createSubscriptions();

        this.selectedSortField = this.sortFieldName;
        this.selectedSortField$.next(this.selectedSortField);
        this.lhpSortFields = [this.sortFieldName, this.sortFieldCreatedDate];
        this.lhpSortFields$.next(this.lhpSortFields.slice());

        this.primaryHeading = 'Reports.leftSideBar.reports';
        this.primaryHeading$.next(this.primaryHeading);

        this.allSharedReportsItem = new LeftSidebarListItem();
        this.allSharedReportsItem.value = this.allSharedReportsKey;
        this.allSharedReportsItem.label = 'Monitor.leftSidebar.reports.allSharedReports';
        this.allSharedReportsItem.icon = 'fa-kit-duotone fa-agpr-shared';
        this.allSharedReportsItem.status = '';
        this.topListItem = this.allSharedReportsItem;
        this.topListItemProvider = new BehaviorSubject(this.topListItem);
    }


    getAutoTagIdsFromTopic(topic: MentionSearchListItemModel): number[] {
        return topic.applyTagIds;
    }

    /**
     * Gets the list of tag options for a list of items that have tags associated with them
     */
    getTagFilterOptions<T>(
        filter: FilterTypeCheckboxModel<number>,
        items: T[],
        getItemTagIds: (item: T) => number[]
    ): void {
        filter.options = [];
        return;
    }


    /**
     * Updates the sidebar based on the route
     * the user has navigated to
    */
    updateFromRoute(route: ReportsRouteModel) {
        this.currentRoute = route;
        this.selectedListItem = null;

        const item = this.dropdownItems.find(option => option.value === DropdownValues.reports);
        this.selectedDropdownItem = item;
        this.selectedDropdownItemProvider.next(this.selectedDropdownItem);

        this.setSelectedListItem(route);
    }

    /**
     * Creates the subscriptions
    */
    private createSubscriptions(): void {
        this.subscriptions.push(
            this.currentUserStateService.getAccount().pipe(
                rxjsFilter(account => !!account)
            ).subscribe(account => {
                if (
                    !account.permissions.includes(userPermissionsEnum.advancedMonitoring)
                    && account.permissions.includes(userPermissionsEnum.socialIntelligence)
                ) {
                    this.dropdownItems = [
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.reports', DropdownValues.reports),
                    ];
                } else {
                    this.dropdownItems = [
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.topics', DropdownValues.topics),
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.coverage', DropdownValues.mentions),
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.briefings', DropdownValues.briefings),
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.alerts', DropdownValues.alerts),
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.subscriptions', DropdownValues.mediaSubscriptions),
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.reports', DropdownValues.reports)
                    ];
                }

                if (account.permissions.includes(userPermissionsEnum.agenticFeature)) {
                    this.dropdownItems.push(
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.automationAgents', DropdownValues.automationAgents)
                    );
                }

                this.dropdownItemsProvider.next(this.dropdownItems);
                if (this.currentRoute) {
                    // To handle race condition, this sometimes will be reached before this.currentRoute being set
                    this.updateFromRoute(this.currentRoute);
                }
            })
        );

        this.subscriptions.push(
            this.reportsService.getReportListItems().subscribe(reports => {
                if (reports) {
                    this.reports.length = 0;
                    this.reports.push(...reports);
                }
            })
        );

        const updateFromRoute$ = this.reportsRoutingService.getCurrentRoute$().pipe(
            tap(route => {
                this.currentRoute = route;
                if (this.dropdownItems) {
                    this.updateFromRoute(route);
                }
            })
        );

        const getCurrentMode$ = updateFromRoute$.pipe(
            map(route => {
                if (route.type === undefined || route.type === null) {
                    return null;
                }

                // This value does not matter, the current mode will always be constant: reports
                return DropdownValues.reports;
            })
        );

        this.subscriptions.push(
            this.createApplyFilterStream(
                this.reportsRoutingService.getModeChange(getCurrentMode$),
                this.getItemsAndPrepareFilters.bind(this),
                this.userPreferencesService.getUserAppPreferences().pipe(
                    tap(userAppPrefs => {
                        const userPreferenceFilters = userAppPrefs.reportsPrefs.defaultFilters;
                        this.reportConfig.filters.forEach(filter => {
                            /** filter ids match the keys for UserAppPreferenceMonitoringCommonFilters */
                            const prop = filter.id;
                            filter.setCurrentValue(userPreferenceFilters[prop]);
                        });
                    })
                ),
            ).subscribe()
        );

        this.subscriptions.push(
            this.router.events.pipe(
                rxjsFilter(e => e instanceof NavigationCancel)
            ).subscribe((e) => {
                if (this.dropdownItems) {
                    this.updateFromRoute(this.currentRoute);
                }
            }),
        );
    }

    /**
     * Sets selected list item based on the route.  If route value available, sets selected list item value
     * equal to the route value.  If no route value available but route type is all saved mentions it
     *  sets selectedListItem value equal to -1
     *
     * @param route the current route
     */
    private setSelectedListItem(route: ReportsRouteModel): void {
        if (route.reportsRouteValueMap) {
            this.selectedListItem = new LeftSidebarListItem();
            this.selectedListItem.value = route.reportsRouteValueMap.reportId;
        } else if (route.type === ReportsRouteTypeEnum.reportSharedReports) {
            this.selectedListItem = new LeftSidebarListItem();
            this.selectedListItem.value = -1;
        }
        this.selectedListItemProvider.next(this.selectedListItem);
    }

    /**
     * Gets a list of items to include in the sidebar dropdown
    */
    getDropdownItems(): Observable<DropdownOptionModel[]> {
        return this.dropdownItemsProvider;
    }

    /**
     * Gets the currently selected dropdown item
    */
    getSelectedDropdownItem(): Observable<DropdownOptionModel> {
        return this.selectedDropdownItemProvider;
    }

    /**
     * Selects an item in the dropdown
     * @param item An item that exists in the dropdown
    */
    selectDropdownItem(item: DropdownOptionModel): void {
        if (this.dropdownItems.indexOf(item) < 0) {
            throw new Error('Item is not in the list');
        }
        this.selectedDropdownItem = item;
        this.selectedDropdownItemProvider.next(item);

        switch (item.value) {
            case DropdownValues.topics:
                this.monitorReportsNavigationService.gotoTopics();
                break;
            case DropdownValues.mediaSubscriptions:
                this.monitorReportsNavigationService.gotoMediaSubscriptions();
                break;
            case DropdownValues.mentions:
                this.monitorReportsNavigationService.gotoSavedCoverageHype();
                break;
            case DropdownValues.briefings:
                this.monitorReportsNavigationService.gotoBriefings();
                break;
            case DropdownValues.alerts:
                this.monitorReportsNavigationService.gotoAlerts();
                break;
            case DropdownValues.reports:
                this.monitorReportsNavigationService.gotoReports();
                break;
            case DropdownValues.automationAgents:
                this.router.navigate(['/', automationAgentsRoutePath]);
                break;
            case DropdownValues.newSearch:
                this.monitorReportsNavigationService.gotoSearch();
                break;
            default:
                break;
        }
    }

    /**
     * Gets currently available items in the list
    */
    getListItems(): Observable<LeftSidebarListItem[]> {
        return this.listItemsProvider;
    }

    getHiddenListItems(): Observable<LeftSidebarListItem[]> {
        return this.hiddenListItemsProvider;
    }

    getListIsComplete(): Observable<boolean> {
        return this.listIsCompleteProvider;
    }

    getMoreListItems(): void {
        // not needed for folders right now
    }

    getSelectedListItem(): Observable<LeftSidebarListItem> {
        return this.selectedListItemProvider;
    }

    getShowNewReportDialogProvider(): Observable<boolean> {
        return this.showNewReportDialogProvider;
    }

    setVisibilityFilter(visibility: ListEntitiesVisibilityEnum = ListEntitiesVisibilityEnum.all) {
        this.visibilityFilter.setCurrentValue(visibility);
        this.applyFilters();
    }

    /**
     * Responds to a user selecting a list item
     * by taking the action that corresponds with
     * the current mode
    */
    selectListItem(item: LeftSidebarListItem): void {

        // if selecting the current active list item, return directly
        if (this.selectedListItem && JSON.stringify(item.value) === JSON.stringify(this.selectedListItem.value).replace(/"/g, '')) {
            return;
        }
        if (item.value === this.allSharedReportsKey) {
            this.monitorReportsNavigationService.goToSharedReports();
        } else {
            this.monitorReportsNavigationService.gotoReport(item.value);
        }
    }

    /**
     * Responds to the user performing an action from
     * a sidebar list item based on the current mode
    */
    listItemAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton): void {
        if (!button.loading) {
            this.reportAction(item, button);
        }
    }

    /**
     * Gets a provider for when reordering the left sidebar is enabled
     */
    getReorderEnabled(): Observable<boolean> {
        return this.reorderEnabledProvider;
    }

    /**
     * Sets the left sidebar's order
     */
    setListItems(items: LeftSidebarListItem[], listIsComplete?: boolean) {
        // for re-ordering
    }

    /**
     * Responds to the user clicking the primary call to action
     * in the sidebar
    */
    amplifyButtonClicked(): void {
        this.showReportAmplifyDialog();
    }

    /**
     * Checks if the primary button should be enabled.
     */
    public isAmplifyButtonEnabled(): Observable<boolean> {
        return of(true);
    }

    /**
     * Opens the report amplify dialog, provided the user can leave the current report
     */
    public showReportAmplifyDialog(): void {
        this.reportsService.showCanLeaveDialog().subscribe(
            result => {
                if (result) {
                    this.coreDialogService.open(ReportCreateDialogComponent);
                }
            }
        );
    }

    /**
     * Gets the current localizations for the left sidebar
     */
    getLocalizations(): Observable<LeftSidebarLocalizations> {
        return this.localizationsProvider;
    }

    /**
     * Performs an action on a report
     */
    private reportAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton): void {
        const action: string = button.key;

        const reportListItem: ReportListItemModel = new ReportListItemModel();
        reportListItem.id = item.value;
        reportListItem.name = item.label;
        reportListItem.groupId = item.groupId;

        button.loading = false;

        switch (action) {
            case ActionsEnum.delete:
                this.reportsService.confirmDeleteReport(reportListItem);
                break;
            case ActionsEnum.hide:
                this.reportsService.updateReportVisibility(reportListItem.id, true, button);
                break;
            case ActionsEnum.show:
                this.reportsService.updateReportVisibility(reportListItem.id, false, button);
                break;
            case ActionsEnum.edit:
                this.reportsService.renameReport(reportListItem);
                break;
            case ActionsEnum.copy:
                this.reportsService.confirmCopyReport(reportListItem);
                break;
            case ActionsEnum.rename:
                this.reportsService.updateReportName(reportListItem.id, reportListItem.name);
                break;
            case ActionsEnum.favorite:
                this.reportsService.updateReportFavorites([reportListItem.id], []);
                break;
            case ActionsEnum.unfavorite:
                this.reportsService.updateReportFavorites([], [reportListItem.id]);
                break;
        }

    }

    /**
     * Handles removing the groupId from all items in a deleted group
     * @param listItems items from the deleted group
     */
    public handleGroupDelete(ids: number[]) {
        this.reportsService.removeAllReportsFromGroup(ids);
    }

    /**
     * Opens GroupSelectionDialog for user selection then adds item to the selected group.
     * @param item item to move on confirm
     */
    public moveListItem(item: LeftSidebarListItem) {
        const groupId = item.groupId ? item.groupId : null;
        const showUncategorized = !!item.groupId;
        this.displayGroupsService.openGroupSelectionDialog(groupId, showUncategorized).subscribe(
            (group: DisplayGroupModel) => {
                this.reportsService.updateReportGroup([item.value], group);
            }
        );
    }

    /**
     * Sets the current localizations
     */
    private setLocalizations(localizations: LeftSidebarLocalizations): void {
        this.localizations = localizations;
        this.localizationsProvider.next(localizations);
    }


    //#region Filters

    /**
     * Resets all of the filters
     */
    private resetFilterOptions(): void {
        this.nameFilter.setValue([]);
        this.createdByFilter.setValue([]);
        this.createdByFilter.options = [];
        this.createdDateFilter.setValue(DateQueryModel.getAllQuery());
        this.modifiedByFilter.setValue([]);
        this.modifiedByFilter.options = [];
        this.modifiedDateFilter.setValue(DateQueryModel.getAllQuery());
        this.visibilityFilter.setValue(null);
        this.visibilityFilter.options = [];
    }




    private applyVisibilityFilter<T extends AuditableModel>(filter: FilterTypeRadioModel<ListEntitiesVisibilityEnum>, items: T[]): T[] {
        const value = filter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
                return items;
            case ListEntitiesVisibilityEnum.visible:
                return items.filter(item => !item.hidden);
            case ListEntitiesVisibilityEnum.hidden:
                return items.filter(item => item.hidden);
            default:
                return items.filter(item => !item.hidden);
        }
    }

    /**
     * Gets the list of items that are currently "hidden" in the LHP, depending on visibility filter applied.
     * @param filter the visibility filter currently applied
     * @param items a list of LHP items
     * @returns a list of the currently hidden items based on the visibility filter
     */
    private getHiddenItemsFromFilteredItems<T extends AuditableModel>(
        filter: FilterTypeRadioModel<ListEntitiesVisibilityEnum>,
        items: T[]
    ): T[] {
        const value = filter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
            case ListEntitiesVisibilityEnum.hidden:
                // There are no "hidden" items that are not currently showing if we are explicitly showing them
                return [];
            case ListEntitiesVisibilityEnum.visible:
            default:
                // can be null as there are no options/starting value for the visibility filter
                return items.filter(item => item.hidden);
        }
    }

    /**
     * Gets the list of options for any string key from a list of objects
     *
     * @param filter A string checkbox filter
     * @param items A list of items
     * @param getItemOptionKey A method for extracting a string key
     * @param enumLabel The prefix for the filter option along with the enum to use to find it
     */
    getEnumStringOptions<T>(
        filter: FilterTypeCheckboxModel<string>,
        items: T[],
        getItemOptionKey: (item: T) => string,
        enumLabel: FilterOptionEnumLabel
    ): void {
        if (!items || items.length < 1) {
            filter.options = [];
            return;
        }

        // Initialize enum counts to 0 for all enum values
        const optionCounts = Object.values(enumLabel.enumType).reduce((o, prop) => {
            o[prop as string] = 0;
            return o;
        }, {} as { [key: string]: number});

        items.forEach(item => {
            const key = getItemOptionKey(item);

            if (optionCounts[key]) {
                optionCounts[key]++;
            } else {
                optionCounts[key] = 1;
            }
        });
        const keys = Object.keys(optionCounts);
        filter.options = keys.map(key => {
            return {
                label: enumLabel.prefix + key,
                count: optionCounts[key],
                value: key
            };
        }).sort((a, b) => b.count - a.count);

        const filtersChanged = this.removeUnavailableOptions(filter);
        if (filtersChanged) {
            this.applyFilters();
        }
    }

    /**
     * Applies the current set of filters to the list items
     */
    applyFilters(): void {
        super.applyFilters();

        let filteredItems: GenericLeftSidebarItem[] = this.reportConfig.listItemSource.slice();

        if (!filteredItems) {
            this.listItems = null;
            this.listItemsProvider.next(this.listItems);

            return;
        }

        filteredItems = this.applyNameFilters(filteredItems);
        filteredItems = this.applyAuditableFilters(filteredItems);

        const allVisibiltyItems = filteredItems.slice();
        const hiddenItems = this.getHiddenItemsFromFilteredItems(this.visibilityFilter, allVisibiltyItems);
        this.hiddenListItems = this.reportConfig
            .convertToSidebarListItems(hiddenItems)
            .filter(item => item.value !== this.allSharedReportsKey);
        this.hiddenListItemsProvider.next(this.hiddenListItems);

        filteredItems = this.applyVisibilityFilter(this.visibilityFilter, filteredItems);

        this.listItems = this.reportConfig.convertToSidebarListItems(filteredItems);
        this.groupListItems();
        this.listItemsProvider.next(this.listItems);
    }

    sortList(groups?: DisplayGroupModel[]): void {
        const topItem = this.listItems.shift();
        super.sortList(groups);
        this.listItems.unshift(topItem);
    }

    /**
     * Based on the current mode, returns an observable that sets and prepares the current filters.
     */
    private getItemsAndPrepareFilters(currentMode: number): Observable<void> {
        const getItems$ = this.reportConfig.listItemStream as Observable<LeftSidebarListItemConvertableModel[]>;
        const type = this.modeToGroupTypeMap.get(this.currentMode);
        const getGroups$ = this.displayGroupsService.setTypeAndGetGroups(type);

        // When the list items emit, we call prepareFilters(). prepareFilters causes an inner subscription in
        // getAuditableFilterOptions. We need a combineLatest here (instead of simple piping) because of this
        // (We need to call the function but also have a subscription set up to handle it if it happens synchronously)
        // TODO: Clean up the getAuditableFilterOptions method to allow easier composition
        return combineLatest([
            this.auditableFilterOptionsComplete$.pipe(
                take(1)
            ),
            getItems$.pipe(
                rxjsFilter(x => !!x),
                tap(() => {
                    this.nameFilter.label = 'Reports.input.reportName';
                    this.setFilters(this.reportConfig.filters);

                    const placeholder = this.reportConfig.quickFilterPlaceholder;
                    const items = this.reportConfig.listItemSource as GenericLeftSidebarItem[];

                    this.setQuickFilterPlaceholder(placeholder);
                    this.getAuditableFilterOptions(items);

                    this.getVisibilityFilterOptions(this.visibilityFilter);
                })
            ),
            getGroups$.pipe(
                map((groups) => {
                    this.listItemGroups = groups.slice();
                })
            )
        ]).pipe(
            mapTo(null),
        );
    }
    //#endregion

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
